import type { DemoMarkDoneResponse } from "../auth/api-client";
import type { LessonTestBlockWorkerResult } from "../python-runtime/protocol";

export type LessonTestVisibilityLevel = "hidden" | "summary" | "detailed";

export type LessonRuntimeMarkDoneSpec = {
  readonly id: string;
  readonly name: string;
  readonly testCode: string;
  readonly isRequired: boolean;
  readonly visibility: LessonTestVisibilityLevel;
  readonly description: string | null;
};

function truncateOneLine(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function blockFullyPassed(block: LessonTestBlockWorkerResult): boolean {
  if (block.loadError) return false;
  return block.cases.length > 0 && block.cases.every((c) => c.ok);
}

function primaryFailureTrace(block: LessonTestBlockWorkerResult): string {
  if (block.loadError) return block.loadError;
  const bad = block.cases.find((c) => !c.ok);
  return bad?.err ?? "";
}

function extractAssertHint(traceback: string): string {
  const t = traceback.trim();
  if (!t) return "";
  const m = /AssertionError:\s*([^\n\r]+)/i.exec(t);
  if (m?.[1]) return m[1].trim();
  const m2 = /AssertionError\s*\n\s*(.+)/im.exec(t);
  if (m2?.[1]) return m2[1].trim().split("\n")[0]?.trim() ?? "";
  const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return lines[lines.length - 1] ?? "";
}

function buildCheckLabel(
  spec: LessonRuntimeMarkDoneSpec,
  block: LessonTestBlockWorkerResult
): { id: string; label: string; passed: boolean } {
  if (blockFullyPassed(block)) {
    return { id: `lt-${spec.id}`, label: spec.name, passed: true };
  }
  const trace = primaryFailureTrace(block);
  const hint = extractAssertHint(trace);
  const teacherNote = (spec.description ?? "").trim();
  const vis = spec.visibility;
  if (vis === "hidden") {
    const tail = teacherNote ? ` ${teacherNote}` : " Adjust your code and run Check My Work again.";
    return { id: `lt-${spec.id}`, label: `${spec.name}: did not pass yet.${tail}`, passed: false };
  }
  if (vis === "summary") {
    const parts: string[] = [`${spec.name}:`];
    if (teacherNote) parts.push(teacherNote);
    if (hint && hint !== teacherNote) parts.push(`(${hint})`);
    else if (!teacherNote && !hint) parts.push("(review the lesson steps and your main.py)");
    else if (!teacherNote && hint) parts.push(`(${hint})`);
    return { id: `lt-${spec.id}`, label: parts.join(" "), passed: false };
  }
  const traceShort = truncateOneLine(trace, 220);
  const primary =
    (hint && hint !== teacherNote ? hint : "") ||
    (traceShort && traceShort !== teacherNote ? traceShort : "") ||
    teacherNote ||
    "Review your program against the lesson instructions.";
  const label =
    teacherNote && primary !== teacherNote ? `${spec.name}: ${teacherNote} (${primary})` : `${spec.name}: ${primary}`;
  return { id: `lt-${spec.id}`, label, passed: false };
}

export function buildMarkDoneFromLessonTestBlocks(
  blocks: LessonTestBlockWorkerResult[],
  specs: readonly LessonRuntimeMarkDoneSpec[]
): DemoMarkDoneResponse {
  const byId = new Map(blocks.map((b) => [b.testId, b]));
  const checks = specs.map((s) => {
    const b = byId.get(s.id);
    if (!b) return { id: `lt-${s.id}`, label: `${s.name}: check did not run.`, passed: false };
    return buildCheckLabel(s, b);
  });
  const passed = checks.every((c) => c.passed);
  return {
    submissionId: `local-${crypto.randomUUID()}`,
    outcome: passed ? "passed" : "failed",
    summary: passed
      ? "All automated lesson checks passed."
      : "Some checks need attention. Read each line below, update your code, then run Check My Work again.",
    checks,
    validationEngine: "lesson_autocheck_browser",
  };
}

export function buildServerTestResults(
  blocks: LessonTestBlockWorkerResult[],
  specs: readonly LessonRuntimeMarkDoneSpec[]
): { lessonTestId: string; passed: boolean; message?: string }[] {
  const byId = new Map(blocks.map((b) => [b.testId, b]));
  return specs.map((s) => {
    const b = byId.get(s.id);
    const passed = b ? blockFullyPassed(b) : false;
    if (passed || s.visibility === "hidden") {
      return { lessonTestId: s.id, passed };
    }
    const trace = b ? primaryFailureTrace(b) : "";
    const msg =
      s.visibility === "summary"
        ? (extractAssertHint(trace).slice(0, 240) || (s.description ?? "").trim() || undefined)
        : truncateOneLine(trace, 500) || (s.description ?? "").trim() || undefined;
    return { lessonTestId: s.id, passed, ...(msg ? { message: msg } : {}) };
  });
}

export function submissionFlagsFromBlocks(
  blocks: LessonTestBlockWorkerResult[],
  specs: readonly LessonRuntimeMarkDoneSpec[]
): { requiredPassed: boolean; optionalPassed: boolean } {
  const byId = new Map(blocks.map((b) => [b.testId, b]));
  const requiredPassed = specs.filter((s) => s.isRequired).every((s) => blockFullyPassed(byId.get(s.id)!));
  const optional = specs.filter((s) => !s.isRequired);
  const optionalPassed = optional.length === 0 || optional.every((s) => blockFullyPassed(byId.get(s.id)!));
  return { requiredPassed, optionalPassed };
}

export function mainPyFromProjectFiles(files: readonly { path: string; content: string }[]): string {
  const main = files.find((f) => f.path === "main.py" || f.path.endsWith("/main.py"));
  return main?.content ?? "";
}
